import React, { useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import { useCurrentProjectId } from '@adsk/alloy-provider';
import {
  useNotifications,
  NOTIFICATION_STATUSES,
} from '@adsk/alloy-react-notification';
import { useLocale } from '@adsk/alloy-react-helpers';
import { useStore } from '@common/helpers/storeProvider';
import { OP_STATUSES } from '@common/constants';
import CategorizedContent from './component';
import useProjectSettings from '../hooks/useProjectSettings';
import { CategorizedContentStoreContext } from '../constants';
import {
  buildEditProjectRequest,
  computeProjectDataChanged,
  isDuplicateNameError,
  massageAPIFields,
} from './utils';
import {
  cancelEdit,
  setIsEditing,
  setIsSaving,
  setMissingRequiredField,
  setProjectDataChanged,
  setState,
  setShowDuplicateNameError,
  setShowModalConfirmation,
  setShowProjectValueError,
} from './store/actions';

const CategorizedContentContainer = ({
  isAdminUser,
  loading: loadingParent,
  onProfileSave,
  showInformationBanner,
  showInProductSettings,
  useGetConstructionApis,
}) => {
  const { T } = useLocale(({ locale }) => require(`../../i18n/${locale}.json`));
  const { showNotification } = useNotifications();
  const { store, dispatch } = useStore(CategorizedContentStoreContext);
  const latestOnProfileSave = useRef(onProfileSave);
  const {
    adminAccessProducts,
    dataGetProject,
    dataPatchProject,
    doFetchPatchProject,
    errorPatchProject,
    setDataGetProject,
  } = useProjectSettings(useGetConstructionApis);
  const projectId = useCurrentProjectId();
  const loading =
    loadingParent || dataGetProject == null || adminAccessProducts == null;
  const {
    ui: {
      missingRequiredField,
      projectDataChanged,
      showModalConfirmation,
      isEditing,
    },
    data: editedData,
    originalData,
  } = store;

  // Update onProfileSave reference
  useEffect(() => {
    latestOnProfileSave.current = onProfileSave;
  }, [onProfileSave]);

  // Set project data in state when fetched
  useEffect(() => {
    if (dataGetProject == null) {
      return;
    }

    dispatch(setState(dataGetProject));
  }, [dispatch, dataGetProject]);

  // compute mandatory fields
  useEffect(() => {
    const missing = !editedData.name || !editedData.type;

    if (missing !== missingRequiredField) {
      dispatch(setMissingRequiredField(missing));
    }
  }, [editedData.name, editedData.type, missingRequiredField, dispatch]);

  // compute project data changed
  useEffect(() => {
    if (editedData == null || originalData == null) return;

    const changed = computeProjectDataChanged(editedData, originalData);

    if (changed !== projectDataChanged) {
      dispatch(setProjectDataChanged(changed));
    }
  }, [editedData, originalData, projectDataChanged, dispatch]);

  // patch project success
  useEffect(() => {
    if (
      dataPatchProject == null ||
      dataGetProject == null ||
      setDataGetProject == null
    ) {
      return;
    }

    setDataGetProject({
      ...dataGetProject,
      ...dataPatchProject,
    });

    dispatch(setIsEditing(false));
    dispatch(setIsSaving(false));
    showNotification({
      status: NOTIFICATION_STATUSES.SUCCESS,
      message: T.translate('SETTINGS_SUCCESS_MESSAGE'),
    });
    latestOnProfileSave.current({
      status: OP_STATUSES.SUCCESS,
      response: dataPatchProject,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dataPatchProject, setDataGetProject]);

  // patch project error
  useEffect(() => {
    if (errorPatchProject === null) return;

    dispatch(setIsSaving(false));
    // Intercept and display the duplicate name field error
    if (isDuplicateNameError(errorPatchProject.errors)) {
      dispatch(setShowDuplicateNameError(true));
    } else {
      showNotification({
        status: NOTIFICATION_STATUSES.ERROR,
        message: T.translate('EDIT_PROJECT_PATCH_ERROR'),
      });
      latestOnProfileSave.current({
        status: OP_STATUSES.ERROR,
        response: errorPatchProject,
      });
    }
  }, [errorPatchProject, dispatch, T, showNotification]);

  const handleEditButtonClick = () => {
    dispatch(setIsEditing(true));
  };

  const handleCancelButtonClick = () => {
    if (projectDataChanged) {
      dispatch(setShowModalConfirmation(true));
    } else {
      dispatch(cancelEdit());
    }
  };

  const handleSaveButtonClick = () => {
    dispatch(setIsSaving(true));
    doFetchPatchProject(
      ...buildEditProjectRequest(
        projectId,
        massageAPIFields(
          editedData.asMutable({ deep: true }),
          originalData.asMutable({ deep: true })
        )
      )
    );
  };

  const handleModalConfirmationConfirm = () => {
    dispatch(setShowProjectValueError(false));
    dispatch(cancelEdit());
  };

  const handleModalConfirmationCancel = () => {
    dispatch(setShowModalConfirmation(false));
  };

  return (
    <CategorizedContent
      adminAccessProducts={adminAccessProducts}
      loading={loading}
      isAdminUser={isAdminUser}
      isEditing={isEditing}
      onCancelButtonClick={handleCancelButtonClick}
      onEditButtonClick={handleEditButtonClick}
      onModalConfirmationCancel={handleModalConfirmationCancel}
      onModalConfirmationConfirm={handleModalConfirmationConfirm}
      onSaveButtonClick={handleSaveButtonClick}
      showInformationBanner={showInformationBanner}
      showInProductSettings={showInProductSettings}
      showModalConfirmation={showModalConfirmation}
    />
  );
};

CategorizedContentContainer.propTypes = {
  isAdminUser: PropTypes.bool,
  loading: PropTypes.bool,
  onProfileSave: PropTypes.func,
  showInformationBanner: PropTypes.bool,
  showInProductSettings: PropTypes.bool,
  useGetConstructionApis: PropTypes.bool,
};

CategorizedContentContainer.defaultProps = {
  isAdminUser: false,
  showInProductSettings: false,
};

export default CategorizedContentContainer;
